import React from 'react';
import { View, Image, Text, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';

// Três "telas" simplificadas usadas dentro dos mockups de iPhone desta página.
// Não são as telas reais do app (que têm toda a lógica de navegação/dados) —
// são uma representação visual fiel o bastante para o admin conferir o efeito
// das cores/imagens escolhidas, sem precisar publicar nada.

export function SplashPreview({ imageUrl, logoUrl, text, buttonBg, buttonText }) {
  // Mesmas cores dos botões da tela de Login real: preenchido usa
  // button.backgroundAuth (fundo) + button.textAuth (texto); o "Login" é
  // transparente com borda/texto em button.backgroundAuth.
  const filledBg = buttonBg || '#A64C12';
  const filledText = buttonText || '#FFFFFF';
  return (
    <View style={[styles.fill, { backgroundColor: '#D9D9D9' }]}>
      {imageUrl ? (
        <Image source={{ uri: imageUrl }} style={styles.fill} resizeMode="cover" />
      ) : null}
      <View style={styles.splashOverlay}>
        <View style={styles.splashLogoArea}>
          {logoUrl ? (
            <Image source={{ uri: logoUrl }} style={styles.splashLogo} resizeMode="contain" />
          ) : null}
          {text ? <Text style={styles.splashText}>{text}</Text> : null}
        </View>

        <View style={styles.splashButtons}>
          <View style={[styles.splashButton, { backgroundColor: filledBg }]}>
            <Text style={[styles.splashButtonText, { color: filledText }]}>Visualizar conteúdo</Text>
          </View>
          <View style={[styles.splashButton, styles.splashButtonOutline, { borderColor: filledBg }]}>
            <Text style={[styles.splashButtonText, { color: filledBg }]}>Login</Text>
          </View>
        </View>
      </View>
    </View>
  );
}

export function LoginPreview({ bgColor, textColor, buttonBg, buttonText, logoUrl }) {
  // Cores fiéis à tela de Login real (componente <Login />):
  //   fundo   = background.auth   (bgColor)
  //   texto/labels/ícones/placeholder/"Esqueceu a senha?" = text.auth (color)
  //   botão   = button.backgroundAuth (buttonBg) + button.textAuth (buttonText)
  //   logo    = logoAuth (logoUrl); sem logo, um losango placeholder.
  // (A status bar do topo é desenhada pelo PhoneMockup.)
  const color = textColor || '#A64C12';
  const bg = bgColor || '#F4F0EB';
  return (
    <View style={[styles.fill, { backgroundColor: bg }]}>
      <View style={styles.loginContent}>
        {logoUrl ? (
          <Image source={{ uri: logoUrl }} style={styles.loginLogo} resizeMode="contain" />
        ) : (
          <View style={[styles.loginIcon, { borderColor: color }]}>
            <View style={[styles.loginIconDot, { backgroundColor: color }]} />
          </View>
        )}

        <Text style={[styles.loginLabel, { color }]}>E-mail</Text>
        <View style={[styles.loginField, { borderColor: color }]}>
          <Feather name="mail" size={10} color={color} />
          <Text style={[styles.loginPlaceholder, { color }]}>digite seu e-mail</Text>
        </View>

        <Text style={[styles.loginLabel, { color }]}>Senha</Text>
        <View style={[styles.loginField, { borderColor: color }]}>
          <Feather name="key" size={10} color={color} />
          <Text style={[styles.loginPlaceholder, { color }]}>digite sua senha</Text>
        </View>

        <Text style={[styles.loginForgot, { color }]}>Esqueceu a senha?</Text>

        <View style={[styles.loginButton, { backgroundColor: buttonBg || color }]}>
          <Text style={[styles.loginButtonText, { color: buttonText || '#FFFFFF' }]}>
            Login
          </Text>
        </View>
      </View>
    </View>
  );
}

export function HomePreview({ appBgColor, tabBgColor, tabIconColor, tabHighlightColor }) {
  // A status bar do topo é desenhada pelo PhoneMockup.
  return (
    <View style={[styles.fill, { backgroundColor: appBgColor || '#F5EFE6' }]}>
      <View style={styles.homeContent}>
        <View style={styles.homeBanner} />
        <View style={styles.homeTilesRow}>
          <View style={styles.homeTile} />
          <View style={styles.homeTile} />
        </View>
        <View style={styles.homeLineShort} />
        <View style={styles.homeTilesRow}>
          <View style={styles.homeTile} />
          <View style={styles.homeTile} />
        </View>
      </View>

      <View style={[styles.homeTabBar, { backgroundColor: tabBgColor || '#FFFFFF' }]}>
        <View style={styles.homeTabItem}>
          <Feather name="home" size={14} color={tabHighlightColor || '#000'} />
          <Text style={[styles.homeTabLabel, { color: tabHighlightColor || '#000' }]}>Início</Text>
        </View>
        <View style={styles.homeTabItem}>
          <Feather name="heart" size={14} color={tabIconColor || '#999'} />
          <Text style={[styles.homeTabLabel, { color: tabIconColor || '#999' }]}>Favoritos</Text>
        </View>
        <View style={styles.homeTabItem}>
          <Feather name="user" size={14} color={tabIconColor || '#999'} />
          <Text style={[styles.homeTabLabel, { color: tabIconColor || '#999' }]}>Perfil</Text>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  fill: {
    ...StyleSheet.absoluteFillObject,
  },

  // Splash / abertura
  splashOverlay: {
    ...StyleSheet.absoluteFillObject,
    paddingHorizontal: 12,
    paddingTop: 30,
    paddingBottom: 14,
    justifyContent: 'space-between',
  },
  splashLogoArea: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  splashLogo: {
    width: '78%',
    height: '26%',
  },
  splashText: {
    marginTop: 10,
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    textShadowColor: 'rgba(0,0,0,0.4)',
    textShadowRadius: 3,
  },
  splashButtons: {
    width: '100%',
    gap: 6,
  },
  splashButton: {
    width: '100%',
    height: 26,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  splashButtonOutline: {
    backgroundColor: 'transparent',
    borderWidth: 1,
  },
  splashButtonText: {
    fontSize: 10,
    fontWeight: '700',
  },

  // Login
  loginContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
  },
  loginIcon: {
    width: 34,
    height: 34,
    borderRadius: 8,
    borderWidth: 1.5,
    transform: [{ rotate: '45deg' }],
    marginBottom: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loginIconDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    transform: [{ rotate: '-45deg' }],
  },
  loginLogo: {
    width: '50%',
    height: 34,
    marginBottom: 22,
  },
  loginLabel: {
    alignSelf: 'flex-start',
    fontSize: 9,
    fontWeight: '600',
    marginBottom: 4,
  },
  loginField: {
    width: '100%',
    minHeight: 24,
    borderRadius: 6,
    borderWidth: 1,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    gap: 6,
  },
  loginPlaceholder: {
    fontSize: 8,
    fontWeight: '500',
  },
  loginForgot: {
    alignSelf: 'flex-end',
    fontSize: 8,
    fontWeight: '600',
    textDecorationLine: 'underline',
    marginBottom: 16,
  },
  loginButton: {
    width: '100%',
    height: 26,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loginButtonText: {
    fontSize: 10,
    fontWeight: '700',
  },

  // Home / início
  homeContent: {
    flex: 1,
    padding: 8,
  },
  homeBanner: {
    width: '100%',
    height: 34,
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.08)',
    marginBottom: 8,
  },
  homeTilesRow: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6,
  },
  homeTile: {
    flex: 1,
    height: 30,
    borderRadius: 5,
    backgroundColor: 'rgba(0,0,0,0.08)',
  },
  homeLineShort: {
    width: '40%',
    height: 5,
    borderRadius: 3,
    backgroundColor: 'rgba(0,0,0,0.1)',
    marginBottom: 6,
    marginTop: 2,
  },
  homeTabBar: {
    flexDirection: 'row',
    height: 34,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  homeTabItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
  },
  homeTabLabel: {
    fontSize: 6,
    fontWeight: '600',
  },
});
